/**
 * Scorecard Truncation Tooltip Alpine Component
 * Displays a tooltip for truncated row and section display names in the scorecard table.
 * Implemeneed for both view mode and manual mode
 */

// State (module-level, shared across component instances)
let tt_state = {
    tooltip: null,
    showTimeout: null,
    hideTimeout: null,
    initialized: false,
    SHOW_DELAY: 300,
    HIDE_DELAY: 100,
};

export default () => ({
    initTruncationTooltipFeature() {
        if (tt_state.initialized) return;
        this.tt_ensureTooltip();
        tt_state.initialized = true;
    },

    destroyTruncationTooltipFeature() {
        if (!tt_state.initialized) return;

        this.tt_clearTimeouts();

        if (tt_state.tooltip && tt_state.tooltip.parentNode) {
            tt_state.tooltip.parentNode.removeChild(tt_state.tooltip);
            tt_state.tooltip = null;
        }

        tt_state.initialized = false;
    },

    tt_ensureTooltip() {
        if (!tt_state.tooltip) {
            const tooltip = document.createElement('div');
            tooltip.id = 'truncation-tooltip';
            tooltip.className = 'fixed z-50 bg-gray-800 text-white text-sm rounded py-1 px-2 w-max max-w-sm break-words shadow-lg pointer-events-none hidden';
            tooltip.setAttribute('role', 'tooltip');
            document.body.appendChild(tooltip);
            tt_state.tooltip = tooltip;
        }
        return tt_state.tooltip;
    },

    tt_isTruncated(element) {
        return element.scrollWidth > element.clientWidth;
    },

    tt_positionTooltip(element) {
        const rect = element.getBoundingClientRect();
        const tooltip = tt_state.tooltip;
        
        const SPACING = 30; // Space between element and tooltip
        const MARGIN = 8; // Margin from viewport edge
        
        // Position to the right
        const left = rect.right + SPACING;
        const top = rect.top + (rect.height / 2);
        
        // Calculate available space to the right
        const availableWidth = window.innerWidth - left - MARGIN;
        
        // Reset max-width first to get natural width
        tooltip.style.maxWidth = '';
        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
        tooltip.style.transform = 'translateY(-50%)';
        
        // Check if tooltip fits, if not, constrain its width
        const tooltipRect = tooltip.getBoundingClientRect();
        if (tooltipRect.width > availableWidth && availableWidth > 100) {
            tooltip.style.maxWidth = `${availableWidth}px`;
        }
        
        // Handle vertical overflow
        const updatedRect = tooltip.getBoundingClientRect();
        if (updatedRect.bottom > window.innerHeight - MARGIN) {
            tooltip.style.top = `${window.innerHeight - updatedRect.height - MARGIN}px`;
            tooltip.style.transform = 'translateY(0)';
        } else if (updatedRect.top < MARGIN) {
            tooltip.style.top = `${MARGIN}px`;
            tooltip.style.transform = 'translateY(0)';
        }
    },

    tt_showTooltip(element, text) {
        this.tt_clearTimeouts();
        
        tt_state.showTimeout = setTimeout(() => {
            const tooltip = this.tt_ensureTooltip();
            tooltip.textContent = text;
            tooltip.classList.remove('hidden');
            this.tt_positionTooltip(element);
        }, tt_state.SHOW_DELAY);
    },

    tt_hideTooltip() {
        this.tt_clearTimeouts();
        
        tt_state.hideTimeout = setTimeout(() => {
            if (tt_state.tooltip) {
                tt_state.tooltip.classList.add('hidden');
            }
        }, tt_state.HIDE_DELAY);
    },

    tt_clearTimeouts() {
        if (tt_state.showTimeout) {
            clearTimeout(tt_state.showTimeout);
            tt_state.showTimeout = null;
        }
        if (tt_state.hideTimeout) {
            clearTimeout(tt_state.hideTimeout);
            tt_state.hideTimeout = null;
        }
    },

    // Public methods to be called from Alpine directives
    tt_onMouseEnter(event) {
        const element = event.target;
        
        if (this.tt_isTruncated(element)) {
            const fullText = element.textContent.trim();
            if (fullText) {
                this.tt_showTooltip(element, fullText);
            }
        }
    },

    tt_onMouseLeave() {
        this.tt_hideTooltip();
    },
});
